import {
  updateGoogleDriveIdMutation,
  savePresentationToFirebase,
} from '../../common/graphql/mutations';
import { CriticalError } from '../../common/lib/errors';
import smalltalk from '../../vendor/smalltalk';
import variables from '../../common/variables';
import { getOrder } from '../reducers/canvasReducer';
import { renameDriveFile, updateDriveFile } from '../../common/storage/google';
import { MODE_TEMPLATE } from '../../common/lib/modes';
import SparkMD5 from 'spark-md5';
import pick from 'lodash-es/pick';
import orderBy from 'lodash-es/orderBy';

const replacer = (key, value) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => {
          sorted[key] = value[key];
          return sorted;
        }, {})
    : value;

export const buildSlideVersion = slide => {
  const _slide = {
    content: {
      // sort objects by order and keys
      objects: orderBy(
        Object.values(slide.content?.objects || {}),
        'ludus.order',
      ).map(o => JSON.stringify(o, replacer)),
      background: slide.content?.background,
    },
    visible: slide.visible,
    templateId: slide.templateId,
    templateVersion: slide.templateVersion,
    transition: {
      id: slide.transition?.id,
      params: slide.transition?.params,
    },
  };

  const version = SparkMD5.hash(JSON.stringify(_slide));
  return version;
};

export const buildPresentationVersion = presentation => {
  const presentationAsVersion = pick(presentation, [
    'canvasDimensions',
    'description',
    'order',
    'title',
  ]);

  const updatedSlideVersion = {};

  const slidesVersions = presentation.order.map(id => {
    if (!presentation.slides[id]) {
      return '';
    }
    if (presentation.slides?.[id]?.version) {
      return presentation.slides[id].version;
    } else {
      const version = buildSlideVersion(presentation.slides[id]);
      updatedSlideVersion[id] = version;
      return version;
    }
  });

  const version = SparkMD5.hash(
    JSON.stringify({
      ...presentationAsVersion,
      slidesVersions,
    }),
  );
  return { version, updatedSlideVersion };
};

/**
 * Build a slide input object suited for the GraphQL mutation
 *
 * @param contentState
 * @param slideId
 * @return {{id: *, content: *, transition: slide.transition|{id, params}}}
 */
const buildSlideInput = (contentState, slideId) => {
  const slide = contentState.presentation.slides[slideId];
  if (slide) {
    const {
      id,
      content,
      transition,
      visible,
      templateId,
      templateVersion,
    } = slide;

    return {
      id,
      content,
      transition,
      visible,
      templateId,
      templateVersion,
    };
  }
  return null;
};

/**
 * Build slide inputs
 *
 * @param contentState
 * @param key
 * @return {*}
 */
const buildSlideInputs = (contentState, key) =>
  contentState.changes[key]
    .map(slideId => buildSlideInput(contentState, slideId))
    .filter(Boolean);

const savePresentationSuccess = (
  presentation,
  added,
  updated,
  deleted,
) => dispatch =>
  new Promise(resolve => {
    const broadcast = {
      id: presentation.id,
      version: presentation.version,
      added,
      updated,
      deleted,
    };

    dispatch({ type: 'SAVE_SUCCEEDED', broadcast });
    dispatch({ type: 'BACKEND_OK' });

    resolve();
  });

const savePresentationError = errorMessage => dispatch => {
  dispatch({ type: 'SAVE_FAILED' });

  return Promise.reject(new CriticalError(errorMessage));
};

export const saveViewerSettings = identifier => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const contentState = getState().contentReducer;
    const { scrollableViewer, hideControls, title } = contentState.presentation;
    const order = getOrder(contentState.presentation);
    savePresentationToFirebase({
      id: identifier,
      name: title,
      scrollableViewer,
      hideControls,
      add: [],
      remove: [],
      update: [],
      order,
      silentUpdate: true,
    }).then(({ data }) => {});
  });

/**
 * Save presentation with its slides
 *
 * @param identifier
 * @return {function(*=, *): Promise<any>}
 */
export const savePresentation = identifier => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const contentState = getState().contentReducer;
    const { saving, dirty } = contentState;
    if (!dirty || saving) {
      resolve();
      return;
    }

    dispatch({ type: 'SAVE_START' });
    const {
      title,
      slug,
      scrollableViewer,
      hideControls,
      password,
      description,
      googleDriveId,
    } = contentState.presentation;
    const order = getOrder(contentState.presentation);

    const content = {};

    const propsToSave = ['slides'];
    const patchToSave =
      getState().lastActionReducer.type === 'UNDO'
        ? contentState.presentation.undone[0]?.inversePatches
        : contentState.presentation.undoable[0]?.patches;

    const authorId = getState().context?.me?.id || window.user.id;
    const firstThumbnail = getState().thumbnailsReducer.generated[order[0]];
    const add = buildSlideInputs(contentState, 'add');
    const update = buildSlideInputs(contentState, 'update');
    const remove = [
      ...contentState.changes.delete,
      // remove slide with no id: can only exist if an update has been made after a slide was already deleted
      ...Object.keys(contentState.presentation.slides).filter(
        id => !contentState.presentation.slides[id],
      ),
    ];

    const { version, updatedSlideVersion } = buildPresentationVersion(
      contentState.presentation,
    );

    if (patchToSave) {
      patchToSave.forEach(patch => {
        if (propsToSave.includes(patch.path[0])) {
          content[`presentations/${identifier}/${patch.path.join('/')}/`] =
            patch.value !== undefined
              ? JSON.parse(
                  JSON.stringify(patch.value).replaceAll('undefined', 'null'),
                )
              : null;

          // ADD LAST AUTHORID IF EDITING A SLIDE
          if (
            patch.path[0] === 'slides' &&
            patch.path.length > 2 &&
            patch.op !== 'remove' &&
            !patch.value?.lastAuthorId
          ) {
            content[
              `presentations/${identifier}/slides/${patch.path[1]}/lastAuthorId`
            ] = authorId;
            if (contentState.presentation.slides[patch.path[1]]) {
              content[
                `presentations/${identifier}/slides/${patch.path[1]}/version`
              ] = contentState.presentation.slides[patch.path[1]].version;
            }
          } else if (
            patch.op === 'remove' &&
            contentState.presentation.slides[patch.path[1]]
          ) {
            content[
              `presentations/${identifier}/slides/${patch.path[1]}/version`
            ] = contentState.presentation.slides[patch.path[1]].version;
          }
        }
      });
    }

    Object.entries(updatedSlideVersion).forEach(([id, version]) => {
      content[`presentations/${identifier}/slides/${id}/version`] = version;
    });

    const thumbnail = firstThumbnail ? firstThumbnail.imageData : null;
    if (googleDriveId) {
      renameDriveFile(googleDriveId, title);

      if (thumbnail) {
        updateDriveFile(googleDriveId, {
          contentHints: {
            thumbnail: {
              image: thumbnail
                .substr(thumbnail.lastIndexOf(',') + 1)
                .replace(/\+/g, '-')
                .replace(/\//g, '_'),
              mimeType: 'image/png',
            },
          },
        });
      }
    }

    savePresentationToFirebase({
      id: identifier,
      version,

      add,
      update,
      remove,
      name: title,
      description,
      slug,
      scrollableViewer,
      hideControls,
      password,
      order,
      authorId,
      content,
    })
      .then(({ data }) => {
        const {
          success,
          presentation,
          added,
          updated,
          deleted,
        } = data.savePresentation;

        if (success) {
          dispatch(
            savePresentationSuccess(
              presentation,
              added || [],
              updated || [],
              deleted || [],
            ),
          ).then(resolve);
        } else {
          dispatch(savePresentationError(variables.errors.default)).catch(
            reject,
          );
        }
      })
      .catch(e => {
        console.error(e);
        smalltalk.alert(
          'Oops',
          `An error happened, please contact support@ludus.one. <br /><code>${e.message}</code>`,
        );
        dispatch(savePresentationError(e.message)).catch(reject);
      });
  });

/**
 * Update googleDriveId
 *
 * @param identifier
 * @param googleDriveId
 * @return {function(*=, *): Promise<any>}
 */
export const updateGoogleDriveId = (identifier, googleDriveId) => (
  dispatch,
  getState,
) =>
  new Promise((resolve, reject) => {
    updateGoogleDriveIdMutation(identifier, googleDriveId)
      .then(result => {
        const { success } = result.data.updatePresentation;
        if (success) {
          resolve();
        } else {
          reject('updatePresentation mutation was unsuccessful');
        }
      })
      .catch(error => {
        reject(error);
      });
  });

/**
 * Delete slides
 *
 * @param slideIds
 * @param newId
 *
 * @return {Function}
 */
export const deleteSlides = (slideIds, newId) => (dispatch, getState) => {
  const presentation = getState().contentReducer.presentation;
  if (presentation.mode === MODE_TEMPLATE) {
    if (Object.keys(presentation.editingTheme?.templates || {}).length > 1) {
      dispatch({ type: 'DELETE_SLIDES', ids: slideIds, newId });
    } else {
      smalltalk.alert('Oops', 'Your theme must have at least have one slide!');
    }
  } else if (presentation.totalSlides - slideIds.length > 0) {
    dispatch({ type: 'DELETE_SLIDES', ids: slideIds, newId });
  } else {
    smalltalk.alert(
      'Oops',
      'Your presentation must have at least have one slide!',
    );
  }
};

/**
 * Dispatch notes update
 *
 * @param slideId
 * @param notes
 * @return {Function}
 */
export const dispatchNotes = (slideId, notes) => dispatch => {
  dispatch({ type: 'NOTES_UPDATED', slideId, notes });
};

/**
 * Skip slides
 *
 * @param ids
 * @return {Function}
 */
export const skipSlides = ids => dispatch => {
  dispatch({ type: 'SLIDES_SKIPPED', ids });
};

/**
 * Unskip slides
 *
 * @param ids
 * @return {Function}
 */
export const unskipSlides = ids => dispatch => {
  dispatch({ type: 'SLIDES_UNSKIPPED', ids });
};
